/**
 * ローカルストレージを使用したユーザー設定管理のユーティリティ関数
 */

/**
 * ユーザー固有のキーを生成
 * @param baseKey 基本キー名
 * @param userId ユーザーID（オプション）
 * @returns ユーザー固有のストレージキー
 */
export const getUserStorageKey = (baseKey: string, userId?: number | null): string => {
  return userId ? `${baseKey}_${userId}` : baseKey;
};

/**
 * ユーザー設定を保存
 * @param key 設定キー
 * @param value 設定値
 * @param userId ユーザーID（オプション）
 */
export const saveUserSetting = <T>(key: string, value: T, userId?: number | null): void => {
  try {
    const storageKey = getUserStorageKey(key, userId);
    localStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    console.error(`設定の保存に失敗しました (${key}):`, error);
  }
};

/**
 * ユーザー設定を取得
 * @param key 設定キー
 * @param defaultValue デフォルト値
 * @param userId ユーザーID（オプション）
 * @returns 保存されている設定値またはデフォルト値
 */
export const getUserSetting = <T>(key: string, defaultValue: T, userId?: number | null): T => {
  try {
    const storageKey = getUserStorageKey(key, userId);
    const savedValue = localStorage.getItem(storageKey);
    
    if (savedValue === null) {
      return defaultValue;
    }
    
    return JSON.parse(savedValue) as T;
  } catch (error) {
    console.error(`設定の取得に失敗しました (${key}):`, error);
    return defaultValue;
  }
};

/**
 * カレンダービューモードの保存用キー
 */
export const CALENDAR_VIEW_MODE_KEY = 'calendar_view_mode';

/**
 * カレンダービューモード保存
 * @param mode ビューモード ('week' または 'month')
 * @param userId ユーザーID（オプション）
 */
export const saveCalendarViewMode = (mode: 'week' | 'month', userId?: number | null): void => {
  saveUserSetting(CALENDAR_VIEW_MODE_KEY, mode, userId);
};

/**
 * カレンダービューモード取得
 * @param userId ユーザーID（オプション）
 * @returns 保存されているビューモードまたはデフォルト値 ('week')
 */
export const getCalendarViewMode = (userId?: number | null): 'week' | 'month' => {
  const mode = getUserSetting<string>(CALENDAR_VIEW_MODE_KEY, 'week', userId);
  // 型安全のためにチェック
  return mode === 'week' || mode === 'month' ? mode : 'week';
};
